/* eslint-disable @typescript-eslint/await-thenable */

import { createModel, RematchDispatch, RematchRootState } from '@rematch/core';
import { RootModel } from '.';
import { ConnectionsState, PwWebConection } from '../types';
import { ConnectionsApi } from '../apis';
import { getLinkToTab, getTeamsContext } from '../utils';
import { AuthService } from '../services';
import * as Constants from '../utils/constants';

const initialConnectionsState: ConnectionsState = {
  connections: undefined,
  connection: undefined,
  tabIsDefault: false,
};

export const connections = createModel<RootModel>()({
  state: initialConnectionsState,
  reducers: {
    resetConnection(state: ConnectionsState): ConnectionsState {
      return {
        ...state,
        connection: undefined,
        connections: undefined,
      };
    },
    setConnections(
      state: ConnectionsState,
      instances: PwWebConection[]
    ): ConnectionsState {
      return {
        ...state,
        connections: instances,
      };
    },
    setConnection(
      state: ConnectionsState,
      connection: PwWebConection | undefined
    ): ConnectionsState {
      return {
        ...state,
        connection,
      };
    },
    setTabIsDefault(
      state: ConnectionsState,
      newValue: boolean
    ): ConnectionsState {
      return {
        ...state,
        tabIsDefault: newValue,
      };
    },
  },
  effects: (dispatch: RematchDispatch<RootModel>) => ({
    async loadConnections(
      _,
      rootState: RematchRootState<RootModel>
    ): Promise<void> {
      try {
        const projectId = rootState.projects.project.id;
        if (projectId === '0') return;
        this.setConnections(await ConnectionsApi.fetchConnections(projectId));
      } catch (error) {
        dispatch.global.handleError({ error });
      }
    },

    async loadConnectionFromTabData(_, rootState: RematchRootState<RootModel>) {
      const project = rootState.projects.project;
      const tabData = rootState.tab.tabData;
      if (!tabData?.iModel || !project.name) return;

      try {
        this.setConnection(
          await ConnectionsApi.fetchConnectionDetails(
            project.id,
            tabData?.connectionId ?? ''
          )
        );
      } catch (error) {
        dispatch.global.handleError({
          error,
          errorMessage:
            'We cannot find the ProjectWise Web connection that you are looking for or it does not exist',
        });
        return;
      }

      await this.loadTabIsDefault();
    },

    async loadTabIsDefault(
      _,
      rootState: RematchRootState<RootModel>
    ): Promise<void> {
      const context = await getTeamsContext();
      if (
        !AuthService.userIsAdmin(await AuthService.getUser()) &&
        !context.entityId
      ) {
        this.setTabIsDefault(false);
        return;
      }

      const currentTabLink = await getLinkToTab(
        (
          await getTeamsContext()
        ).entityId
      );

      const currentTabIsDefault =
        rootState.projects.project.solutionType ===
        Constants.SolutionTypes.projectWise365
          ? !!rootState.connections.connections &&
            rootState.connections.connections.every(
              c => c.defaultTab === currentTabLink
            )
          : !!rootState.connections.connection &&
            rootState.connections.connection.defaultTab === currentTabLink;

      const defaultTabNotSet =
        rootState.projects.project.solutionType ===
        Constants.SolutionTypes.projectWise365
          ? !!rootState.connections.connections &&
            rootState.connections.connections.every(c => !c.defaultTab)
          : !!rootState.connections.connection &&
            !rootState.connections.connection.defaultTab;

      this.setTabIsDefault(
        (context.entityId && currentTabIsDefault) ||
          (!context.entityId && defaultTabNotSet)
      );
    },
  }),
});
